export interface BaseFilter {
  value: string | string[];
  displayValue: string | string[];
}

export interface DurationFilter extends BaseFilter {
  startDate: string;
  endDate: string;
}

export enum LeaseDuration {
  WEEKS = "weeks",
  MONTHS = "months",
}

export enum BudgetDuration {
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

export enum Currency {
  GBP = "GBP",
  USD = "USD",
  EUR = "EUR",
}

export interface LeaseRange {
  min_lease: number;
  max_lease: number;
  unit: LeaseDuration;
}

export interface BudgetRange {
  min_budget: number;
  max_budget: number;
  duration: BudgetDuration;
  currency: Currency;
}

export interface CommunicationPageFilters {
  mode: BaseFilter;
  duration: DurationFilter;
  campaign: BaseFilter;
  budget: { value: BudgetRange[]; displayValue: string[] };
  destinationCountry: BaseFilter;
  sourceCountry: BaseFilter;
  city: BaseFilter;
  university: BaseFilter;
  tags: { value: string[]; displayValue: string[] };
  roomType: { value: string[]; displayValue: string[] };
  property: { value: string[]; displayValue: string[] };
  utmSources: { value: string[]; displayValue: string[] };
  utmMedium: { value: string[]; displayValue: string[] };
  attributionChannel: { value: string[]; displayValue: string[] };
  standardMedium: { value: string[]; displayValue: string[] };
  standardSource: { value: string[]; displayValue: string[] };
  deviceType: { value: string[]; displayValue: string[] };
  lease: { value: LeaseRange[]; displayValue: string[] };
  assignedAgents: { value: string[]; displayValue: string[] };
  leadType: BaseFilter;
  viewing: boolean;
  repeatLead: boolean;
  botVersion: { value: string[]; displayValue: string[] };
  visitorPresence: BaseFilter;
}

export interface CampaignCommunicationPageFilters {
  mode: BaseFilter;
  campaign: BaseFilter;
  status: BaseFilter;
  outcome: BaseFilter;
}

export interface CampaignsPageFilters {
  category: BaseFilter;
  status: BaseFilter;
  user: BaseFilter;
  useCase: BaseFilter;
  branch: BaseFilter;
  startedOn: {
    value: {
      from: string;
      to: string;
    };
    displayValue: {
      from: string;
      to: string;
    };
  };
  createdOn: {
    value: {
      from: string;
      to: string;
    };
    displayValue: {
      from: string;
      to: string;
    };
  };
}

// Root filter state organized by page'
export interface CampaignTemplatesPageFilters {
  category: BaseFilter;
  user: BaseFilter;
  useCase: BaseFilter;
  branch: BaseFilter;
  createdOn: {
    value: {
      from: string;
      to: string;
    };
    displayValue: {
      from: string;
      to: string;
    };
  };
}

export interface CampaignLogsFilters {
  campaign: BaseFilter;
  user: BaseFilter;
}

export interface CampaignExecutionLogsFilters {
  mode: BaseFilter;
  dateRange: {
    value: {
      from: string;
      to: string;
    };
    displayValue: {
      from: string;
      to: string;
    };
  };
  status: BaseFilter;
}

export interface CrmLeadsFilters {
  duration: DurationFilter;
  communicationType: BaseFilter;
  sourceCountry: BaseFilter;
  destinationCountry: BaseFilter;
  destinationCity: BaseFilter;
  university: BaseFilter;
  assignedAgents: {
    value: string[];
    displayValue: string[];
  };
  tags: {
    value: string[];
    displayValue: string[];
  };
  roomType: {
    value: string[];
    displayValue: string[];
  };
  property: {
    value: string[];
    displayValue: string[];
  };
  utmSources: {
    value: string[];
    displayValue: string[];
  };
  utmMedium: {
    value: string[];
    displayValue: string[];
  };
  attributionChannel: {
    value: string[];
    displayValue: string[];
  };
  standardMedium: {
    value: string[];
    displayValue: string[];
  };
  standardSource: {
    value: string[];
    displayValue: string[];
  };
  deviceType: {
    value: string[];
    displayValue: string[];
  };
  academicYear: {
    value: string[];
    displayValue: string[];
  };
  leadStage: {
    value: string[];
    displayValue: string[];
  };
  dealStage: {
    value: string[];
    displayValue: string[];
  };
  dealPropertyName: {
    value: string[];
    displayValue: string[];
  };
  dealRoomType: {
    value: string[];
    displayValue: string[];
  };
  dealAcademicYear: {
    value: string[];
    displayValue: string[];
  };
  dealAvailabilityTitle: {
    value: string[];
    displayValue: string[];
  };
  dealCity: {
    value: string[];
    displayValue: string[];
  };
  dealAssignee: {
    value: string[];
    displayValue: string[];
  };
  dealRoomNumber: BaseFilter;
  leadType: BaseFilter;
  viewing: boolean;
  repeatLead: boolean;
  bedCount: BaseFilter;
  propertyCount: BaseFilter;
  activeFollowUps: boolean;
  leadsNotUpdated30Days: boolean;
  lastActivity: BaseFilter;
}

export interface AnalyticsFilters {
  duration: DurationFilter;
  communicationType: BaseFilter;
  modality: BaseFilter;
}

export interface BillingFilters {
  duration: DurationFilter;
  communicationType: BaseFilter;
}

export interface PlaygroundPageFilters {
  duration: DurationFilter;
}

export interface PlaygroundLogsFilters {
  changesMadeOn: BaseFilter;
  createdOn: {
    value: {
      from: string;
      to: string;
    };
    displayValue: {
      from: string;
      to: string;
    };
  };
}

export interface CommunicationAnalyticsFilters {
  mode: BaseFilter;
  duration: DurationFilter;
}

export interface CrmInboxFilters {
  duration: DurationFilter;
  assignedAgents: {
    value: string[];
    displayValue: string[];
  };
}

export type ViewingsSortKeyFilter =
  | "scheduled_at"
  | "created_at"
  | "updated_at"
  | "";

export type ViewingsSortOrderFilter = "asc" | "desc" | "";

export interface ViewingsFilters {
  sort_key: ViewingsSortKeyFilter;
  sort_order: ViewingsSortOrderFilter;
  displayValue: string;
  scheduledOn: {
    value: { from: string; to: string };
    displayValue: { from: string; to: string };
  };
  createdOn: {
    value: { from: string; to: string };
    displayValue: { from: string; to: string };
  };
  property: { value: string[]; displayValue: string[] };
  attendeeResponse: { value: string[]; displayValue: string[] };
}

export interface FilterState {
  communications: CommunicationPageFilters;
  campaignCommunications: CampaignCommunicationPageFilters;
  campaigns: CampaignsPageFilters;
  campaignTemplates: CampaignTemplatesPageFilters;
  campaignLogs: CampaignLogsFilters;
  campaignExecutionLogs: CampaignExecutionLogsFilters;
  crmLeads: CrmLeadsFilters;
  analytics: AnalyticsFilters;
  billing: BillingFilters;
  playground: PlaygroundPageFilters;
  playgroundLogs: PlaygroundLogsFilters;
  communicationAnalytics: CommunicationAnalyticsFilters;
  crmInbox: CrmInboxFilters;
  viewings: ViewingsFilters;
}
